<template>
  <div class="projects-view">
    <!-- Fejléc -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">Projektek</h1>
      </div>
      <router-link to="/projects/create" class="btn btn-primary">
        <i class="fas fa-plus"></i>
        Új projekt
      </router-link>
    </div>

    <!-- Aktív projektek szekció -->
    <div class="active-projects-section" v-if="activeProjects.length > 0 && false">
      <h2 class="section-title">
        Aktív projektek
        <span class="section-badge">{{ activeProjects.length }}</span>
      </h2>
      <div class="row">
        <div class="col-lg-4 col-md-6 mb-4" v-for="project in activeProjects" :key="'active-' + project.id">
          <div class="project-card">
            <div class="project-header">
              <Status :status="project.status" :show-icon="true" />
              <!-- <div class="project-menu">
                <button class="btn-menu" @click="toggleProjectMenu('active-' + project.id)">
                  <i class="fas fa-ellipsis-v"></i>
                </button>
                <div class="project-menu-dropdown" v-show="activeMenu === 'active-' + project.id">
                  <button @click="editProject(project)">Szerkesztés</button>
                  <button @click="duplicateProject(project)">Duplikálás</button>
                  <button @click="deleteProject(project)" class="text-danger">Törlés</button>
                </div>
              </div> -->
            </div>

            <div class="project-content">
              <h3 class="project-title">{{ project.name }}</h3>

              <div class="project-code">{{ project.project_code }}</div>

              <!-- Gyártások megjelenítése -->
              <div class="project-productions" v-if="project.productions?.length > 0">
                <div class="production-item" v-for="production in project.productions" :key="production.id">
                  <div class="production-header">
                    <span class="production-code" v-html="production.production_code_formatted"></span>
                    <span class="production-status">{{ production.status }}</span>
                  </div>
                  <div class="production-details">
                    <i class="fas fa-calendar-alt"></i>
                    Várható elkészülés: {{ formatDate(production.deadline) }}
                    <br>
                    <i class="fas fa-box"></i>
                    {{ production.products_count }} termék
                  </div>
                </div>
              </div>

              <!-- Pénzügyi összefoglaló -->
              <div class="financial-summary">
                <!-- Előleg státusz -->
                <div class="financial-item advance-status"
                  v-if="project.project_balance.waiting_on_money.gross > 0 || project.project_balance.paid.gross > 0">
                  <div class="financial-icon">
                    <i class="fas fa-coins"></i>
                  </div>
                  <div class="financial-content">
                    <div class="financial-label">Előleg</div>
                    <div class="financial-values">
                      <div class="paid-amount" v-if="project.project_balance.paid.gross > 0">
                        <i class="fas fa-check-circle text-success"></i>
                        {{ formatCurrency(project.project_balance.paid.gross) }}
                      </div>
                      <div class="waiting-amount" v-if="project.project_balance.waiting_on_money.gross > 0">
                        <i class="fas fa-clock text-warning"></i>
                        {{ formatCurrency(project.project_balance.waiting_on_money.gross) }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Projekt érték (ha van) -->
                <div class="financial-item project-value" v-if="project.project_balance.project_sum_value.gross > 0">
                  <div class="financial-icon">
                    <i class="fas fa-calculator"></i>
                  </div>
                  <div class="financial-content">
                    <div class="financial-label">Projekt érték</div>
                    <div class="financial-amount">
                      {{ formatCurrency(project.project_balance.project_sum_value.gross) }}
                    </div>
                  </div>
                </div>

                <!-- Végszámla státusz -->
                <div class="financial-item invoice-status">
                  <div class="financial-icon">
                    <i class="fas fa-file-invoice"
                      :class="project.project_balance.has_final_invoice ? 'text-success' : 'text-muted'"></i>
                  </div>
                  <div class="financial-content">
                    <div class="financial-label">Végszámla</div>
                    <div class="financial-status">
                      <span class="status-indicator"
                        :class="project.project_balance.has_final_invoice && project.project_balance.final_invoice_paid ? 'status-done' : 'status-pending'">
                        {{ project.project_balance.has_final_invoice ? 'Kiállítva' : 'Függőben' }} {{
                          project.project_balance.has_final_invoice && !project.project_balance.final_invoice_paid ?
                            '/Fizetésre vár' : '' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Várható elkészülés megjelenítése -->
              <div class="project-deadline" v-if="project.farthest_deadline">
                <i class="fas fa-calendar-alt"></i>
                <span class="deadline-label">Legkésőbbi várható elkészülés:</span>
                <span class="deadline-date">{{ formatDate(project.farthest_deadline) }}</span>
              </div>
            </div>
            <div class="project-footer">
              <button class="btn btn-primary btn-sm" @click="openProject(project)">
                Megnyitás
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Összes projekt táblázat -->
    <Table :data="projects" :columns="tableColumns" :loading="loading" :pagination="pagination" :sort-by="sortBy"
      :sort-direction="sortDirection" :clickable-rows="false" title="Összes projekt"
      :total-count="pagination ? pagination.total : projects.length" loading-text="Projektek betöltése..."
      empty-text="Nincsenek projektek" empty-icon="fas fa-project-diagram" @sort="handleSort"
      @page-change="handlePageChange" @row-click="handleRowClick">
      
      <!-- Szűrők slot  -->
      <template #filters>
        <div class="filters-container">
          <!-- Keresés -->
          <div class="search-wrapper">
            <div class="search-input">
              <i class="fas fa-search"></i>
              <input type="text" class="form-control" placeholder="Keresés projektek között..." v-model="searchQuery"
                @input="debouncedSearch">
            </div>
          </div>

          <!-- Státusz gombok -->
          <div class="status-filters">
            <button class="btn btn-primary" :class="{ active: statusFilter === '' }" @click="setStatusFilter('')">
              <i class="fas fa-list"></i>
              Összes
              <span class="filter-count" v-if="statusFilter === ''">{{ pagination?.total || 0 }}</span>
            </button>

            <button class="status-filter-btn status-new" :class="{ active: statusFilter === 'new' }"
              @click="setStatusFilter('15')">
              <i class="fas fa-star"></i>
              Feldolgozás alatt
            </button>

            <button class="status-filter-btn status-in-progress" :class="{ active: statusFilter === 'progress' }"
              @click="setStatusFilter('progress')">
              <i class="fas fa-clock"></i>
              Gyártás alatt
            </button>

            <button class="status-filter-btn status-completed" :class="{ active: statusFilter === 'completed' }"
              @click="setStatusFilter('6')">
              <i class="fas fa-check-circle"></i>
              Teljesült
            </button>

            <!-- Szűrő visszaállítás -->
            <button class="btn btn-outline-secondary btn-sm reset-btn" @click="resetFilters"
              v-if="statusFilter || searchQuery">
              <i class="fas fa-times"></i>
              Törlés
            </button>
          </div>
        </div>
      </template>

      <!-- Név mező -->
      <template #cell-name="{ item }">
        <div class="project-name-cell">
          <router-link :to="`/projects/${item.id}`" class="project-title">
            {{ item.name }}
          </router-link>
          <div class="financial-amount">
            {{ formatCurrency(item.project_balance.project_sum_value.gross) }}
          </div>
          <div class="project-code">{{ item.project_code }}</div>
        </div>
      </template>
      <!-- Státsuz -->
      <template #cell-status="{ item }">
        <Status :status="item.status" :show-icon="true" />
      </template>
      <!-- Gyártás -->
      <!-- <template #cell-productions="{ item }">
        <div class="productions-cell">
          <div class="productions-count">
            <i class="fas fa-cog"></i>
            {{ item.productions.length }} gyártás
          </div>
          <div class="productions-list" v-if="item.productions.length > 0">
            <div class="production-mini" v-for="production in item.productions.slice(0, 2)" :key="production.id">
              <span class="production-code" v-html="production.production_code_formatted"></span>

            </div>
            <div class="production-more" v-if="item.productions.length > 2">
              +{{ item.productions.length - 2 }} további
            </div>
          </div>
        </div>
      </template> -->
      <!-- Megrendelés -->
      <template #cell-ordered="{ item }">
        <div class="deadline-cell" v-if="item.created_at">
          <i class="fas fa-calendar-alt"></i>
          {{ formatDate(item.created_at) }}
        </div>
        <span class="text-muted" v-else>-</span>
      </template>
      <!-- Várható elkészülés -->
      <template #cell-deadline="{ item }">
        <div class="deadline-cell" v-if="item.farthest_deadline">
          <i class="fas fa-calendar-alt"></i>
          {{ formatDate(item.farthest_deadline) }}
        </div>
        <span class="text-muted" v-else>-</span>
      </template>
      <!-- Várható elkészülés -->
      <template #cell-product_types="{ item }">
        <div class="product-types-cell" v-if="item.product_types && Object.keys(item.product_types).length > 0">
          <div class="product-type-tags">
            <span 
              class="product-type-tag" 
              v-for="(type, key) in item.product_types" 
              :key="key"
            >
              <i class="fas fa-box"></i>
              {{ type }}
            </span>
          </div>
        </div>
        <span class="text-muted" v-else>-</span>
      </template>
      <!-- Számlázási -->
      <template #cell-billing="{ item }">
        <BillingStatus :has-proforma-invoice="item.has_proforma_invoice" :project-id="item.id"
          :proforma-invoice-paid="item.proforma_invoice_paid" :has-advance-invoice="item.has_advance_invoice"
          :has-final-invoice="item.has_final_invoice" :final-invoice-paid="item.final_invoice_paid"
          :project-balance="item.project_balance" :show-summary="false" />
      </template>

      <template #cell-actions="{ item }">
        <div class="actions-cell">
            <button 
              v-if="item.has_price_offer" 
              @click.stop="openPriceOfferModal(item)"
              class="btn btn-outline-primary"
              title="Árajánlat megtekintése"
            >
              <i class="fas fa-file-pdf"></i>
            </button>
            <button v-else class="btn btn-outline-primary" disabled title="Nincs árajánlat" aria-disabled="true">
              <i class="fas fa-file-pdf"></i>
            </button>
            <router-link :to="`/projects/${item.id}`" class="btn btn-outline-primary">
              <i class="fas fa-eye"></i>
            </router-link>
          </div>
      </template>


      <template #empty-actions>
        <button class="btn btn-primary" @click="createProject">
          <i class="fas fa-plus"></i>
          Első projekt létrehozása
        </button>
      </template>
    </Table>

    <!-- File Viewer Modal -->
    <FileViewerModal
      :show="modalData.show"
      :file-url="modalData.url"
      :filename="modalData.filename"
      :title="modalData.title"
      :loading="modalData.loading"
      :error="modalData.error"
      :is-image="modalData.isImage"
      :is-pdf="modalData.isPdf"
      @close="closeModal"
      @retry="retryLoadFile"
    />
  </div>
</template>

<script>
import apiService from '@/services/apiService'
import Table from '@/components/Table.vue'
import Status from '@/components/helpers/Status.vue'
import BillingStatus from '@/components/helpers/BillingStatus.vue'
import FileViewerModal from '@/components/helpers/FileViewerModal.vue'

export default {
  name: 'ProjectsView',
  components: {
    Table,
    Status,
    BillingStatus,
    FileViewerModal
  },
  data() {
    return {
      searchQuery: '',
      statusFilter: '',
      sortBy: 'created_at',
      sortDirection: 'desc',
      currentPage: 1,
      activeMenu: null,
      activeProjects: [],
      projects: [],
      statuses: [],
      pagination: null,
      loading: true,
      searchTimeout: null,
      
      // Modal adatok
      modalData: {
        show: false,
        url: '',
        isImage: false,
        isPdf: false,
        project: null,
        title: '',
        loading: false,
        error: false,
        filename: ''
      },

      // Táblázat oszlopok
      tableColumns: [
        {
          key: 'name',
          label: 'Projekt',
          sortable: true,
          width: 'sm'
        },
        {
          key: 'status',
          label: 'Státusz',
          sortable: true,
          align: 'center',
          width: 'sm'
        },
        {
          key: 'ordered',
          label: 'Megrendelve',
          sortable: false,
          align: 'left',
          width: 'sm'
        },
        {
          key: 'deadline',
          label: 'Várható elkészülés',
          sortable: false,
          align: 'left',
          width: 'sm'
        },
        {
          key: 'product_types',
          label: 'Termék(ek)',
          sortable: false,
          align: 'left',
          width: 'sm'
        },
        {
          key: 'billing',
          label: 'Számlázás',
          sortable: false,
          width: 'sm'
        },
        {
          key: 'actions',
          label: 'Műveletek',
          sortable: false,
          align: 'center',
          width: 'sm'
        }
      ]
    }
  },
  methods: {
    async loadProjects(page = 1) {
      try {
        this.loading = true

        const params = {
          page: page,
        }

        if (this.searchQuery) params.search = this.searchQuery
        if (this.statusFilter) params.status = this.statusFilter
        if (this.sortBy) params.sort = this.sortBy
        if (this.sortDirection) params.direction = this.sortDirection

        const response = await apiService.get('/projects', { params })

        if (response.data) {
          this.activeProjects = response.data.active_projects || []
          this.projects = response.data.projects || []
          this.statuses = response.data.statuses || []
          this.pagination = response.data.pagination || null
          this.currentPage = page
        }

      } catch (error) {
        console.error('Hiba a projektek betöltésekor:', error)
        this.$swal.fire({
          title: 'Hiba',
          text: 'Nem sikerült betölteni a projekteket',
          icon: 'error'
        })
      } finally {
        this.loading = false
      }
    },

    setStatusFilter(status) {
      this.statusFilter = status
      this.currentPage = 1
      this.loadProjects(1)
    },

    debouncedSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.applyFilters()
      }, 500)
    },

    applyFilters() {
      this.currentPage = 1
      this.loadProjects(1)
    },

    handleSort({ key, direction }) {
      this.sortBy = key
      this.sortDirection = direction
      this.applyFilters()
    },

    handlePageChange(page) {
      this.loadProjects(page)
    },

    handleRowClick({ item }) {
      this.openProject(item)
    },
    async get() {
      const res = await apiService.get('/projects/5463');
      console.log(res.data);
    },
    resetFilters() {
      this.searchQuery = ''
      this.statusFilter = ''
      this.sortBy = 'created_at'
      this.sortDirection = 'desc'
      this.currentPage = 1
      this.loadProjects(1)
    },

    getStatusClass(status) {
      if (!status) return 'status-new'

      const position = status.position
      if (position <= 1) return 'status-new'
      if (position <= 3) return 'status-processing'
      if (position <= 4) return 'status-pricing'
      if (position <= 7) return 'status-quote'
      if (position <= 19) return 'status-production'
      if (position <= 27) return 'status-assembly'
      if (position <= 31) return 'status-delivery'
      if (position >= 32) return 'status-completed'
      return 'status-new'
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('hu-HU')
    },

    formatCurrency(amount) {
      if (!amount) return '-'
      return new Intl.NumberFormat('hu-HU', {
        style: 'currency',
        currency: 'HUF',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount)

    },

    createProject() {
      this.$swal.fire({
        title: 'Új projekt',
        text: 'Itt nyílna meg az új projekt létrehozása form',
        icon: 'info'
      })
    },

    editProject(project) {
      this.$swal.fire({
        title: 'Projekt szerkesztése',
        text: `${project.name} (${project.project_code}) szerkesztése`,
        icon: 'info'
      })
      this.activeMenu = null
    },

    duplicateProject(project) {
      this.$swal.fire({
        title: 'Projekt duplikálása',
        text: `${project.name} (${project.project_code}) duplikálása`,
        icon: 'info'
      })
      this.activeMenu = null
    },

    async deleteProject(project) {
      const result = await this.$swal.fire({
        title: 'Projekt törlése',
        text: `Biztosan törli a "${project.name}" (${project.project_code}) projektet?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Igen, törlöm',
        cancelButtonText: 'Mégse',
        confirmButtonColor: '#dc3545'
      })

      if (result.isConfirmed) {
        try {
          await apiService.delete(`/projects/${project.id}`)
          this.$swal.fire('Törölve!', 'A projekt sikeresen törölve.', 'success')
          this.loadProjects(this.currentPage)
        } catch (error) {
          this.$swal.fire('Hiba!', 'Nem sikerült törölni a projektet.', 'error')
        }
      }
      this.activeMenu = null
    },

    viewProject(project) {
      const productionsList = project.productions.map(p =>
        `<li>${p.production_code_formatted} - ${p.status.name}</li>`
      ).join('')

      this.$swal.fire({
        title: 'Projekt részletei',
        html: `
          <div class="text-left">
            <p><strong>Név:</strong> ${project.name}</p>
            <p><strong>Kód:</strong> ${project.project_code}</p>
            <p><strong>Státusz:</strong> ${project.status.name}</p>
            ${project.advance_payment_limit ? `<p><strong>Előleg limit:</strong> ${this.formatCurrency(project.advance_payment_limit)}</p>` : ''}
            ${project.farthestDeadline ? `<p><strong>Várható elkészülés:</strong> ${this.formatDate(project.farthestDeadline)}</p>` : ''}
            <p><strong>Gyártások (${project.productions.length} db):</strong></p>
            <ul>${productionsList}</ul>
          </div>
        `,
        icon: 'info',
        width: '600px'
      })
    },

    openProject(project) {
      this.$router.push(`/projects/${project.id}`)
    },

    toggleProjectMenu(projectId) {
      this.activeMenu = this.activeMenu === projectId ? null : projectId
    },

    async openPriceOfferModal(project) {
      if (!project || !project.has_price_offer || !project.price_offer_url) {
        console.error('Price offer not available');
        return;
      }

      // Megnyitjuk a modalt loading állapotban
      this.modalData = {
        show: true,
        url: '',
        isImage: false,
        isPdf: true, // Árajánlat általában PDF
        project: project,
        title: `Árajánlat - ${project.name}`,
        loading: true,
        error: false,
        filename: `arajanlat_${project.project_code}.pdf`
      };

      try {
        await this.loadPriceOffer(project);
      } catch (error) {
        console.error('Error loading price offer:', error);
        this.modalData.loading = false;
        this.modalData.error = true;
      }
    },

    async loadPriceOffer(project) {
      try {
        console.log('Loading price offer:', project.price_offer_url);

        const response = await apiService.get(project.price_offer_url, {
          responseType: 'blob',
          timeout: 30000,
          headers: {
            'Accept': '*/*'
          }
        });

        console.log('Response received:', {
          status: response.status,
          headers: response.headers,
          dataType: typeof response.data,
          dataSize: response.data?.size || 'unknown'
        });

        if (!response.data || response.data.size === 0) {
          throw new Error('No file data received');
        }

        let contentType = 'application/pdf';

        if (response.headers) {
          contentType = response.headers['content-type'] ||
            response.headers['Content-Type'] ||
            contentType;
        }

        const blob = new Blob([response.data], { type: contentType });
        const fileUrl = URL.createObjectURL(blob);

        const filename = this.modalData.filename;
        const fileExtension = filename.split('.').pop()?.toLowerCase() || '';

        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(fileExtension) ||
          contentType.startsWith('image/');

        const isPdf = fileExtension === 'pdf' || contentType.includes('pdf');

        this.modalData.url = fileUrl;
        this.modalData.isImage = isImage;
        this.modalData.isPdf = isPdf;
        this.modalData.loading = false;
        this.modalData.error = false;

      } catch (error) {
        console.error('Error loading price offer:', error);
        this.modalData.loading = false;
        this.modalData.error = true;
      }
    },

    async retryLoadFile() {
      if (this.modalData.project) {
        this.modalData.loading = true;
        this.modalData.error = false;
        await this.loadPriceOffer(this.modalData.project);
      }
    },

    closeModal() {
      if (this.modalData.url) {
        URL.revokeObjectURL(this.modalData.url);
      }

      this.modalData = {
        show: false,
        url: '',
        isImage: false,
        isPdf: false,
        project: null,
        title: '',
        loading: false,
        error: false,
        filename: ''
      };
    },
  },

  async mounted() {
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.project-menu') && !e.target.closest('.dropdown')) {
        this.activeMenu = null
      }
    })

    await this.loadProjects()
  }
}
</script>

<style scoped>
.projects-view {
  animation: fadeIn 0.3s ease-in;
}

/* Fejléc */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e9ecef;
}

.page-title {
  font-size: 28px;
  font-weight: 600;
  color: #333;
  margin: 0 0 5px 0;
}

.page-subtitle {
  color: #6c757d;
  margin: 0;
}

/* Aktív projektek szekció */
.active-projects-section {
  margin-bottom: 40px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-badge {
  background: #007bff;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

/* Projekt kártyák (aktív projektek) */
.project-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.project-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.project-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 0 20px;
}

.project-status {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.project-menu {
  position: relative;
}

.btn-menu {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.btn-menu:hover {
  background-color: #f8f9fa;
}

.project-menu-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  min-width: 150px;
}

.project-menu-dropdown button {
  display: block;
  width: 100%;
  padding: 8px 15px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.project-menu-dropdown button:hover {
  background-color: #f8f9fa;
}

.project-content {
  padding: 20px;
  flex: 1;
}

.project-title {
  font-size: 16px;
  font-weight: 600;
  color: #1d4699 !important;
  text-decoration: none;
  margin: 0 0 5px 0;
}

.project-code {
  font-size: 14px;
  color: #434343 !important;
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-block;
  margin-bottom: 15px;
}

.project-productions {
  margin-bottom: 15px;
}

.production-item {
  background: #f8f9fa;
  padding: 10px;
  border-radius: 8px;
  margin-bottom: 8px;
  border-left: 4px solid #007bff;
}

.production-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
}

.production-code {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  font-weight: 600;
}

.production-status {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 12px;
  background: #e9ecef;
  color: #495057;
}

.production-details {
  font-size: 12px;
  color: #6c757d;
}

.project-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 15px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #6c757d;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 6px;
}

.meta-item i {
  width: 16px;
  text-align: center;
  color: #007bff;
}

.project-footer {
  padding: 20px;
  border-top: 1px solid #f8f9fa;
  display: flex;
  gap: 10px;
}

.project-footer .btn {
  flex: 1;
}

/* Táblázat cellák */
.search-input {
  position: relative;
}

.search-input i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
}

.search-input input {
  padding-left: 40px;
}

.project-name-cell .project-title {
  font-weight: 600;
  color: #495057;
  margin-bottom: 2px;
}

.project-name-cell .project-code {
  font-size: 12px;
  color: #6c757d;
  font-family: 'Courier New', monospace;
}



.productions-cell {
  font-size: 13px;
}

.productions-count {
  font-weight: 500;
  margin-bottom: 5px;
}

.productions-count i {
  color: #007bff;
  margin-right: 5px;
}

.production-mini {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #6c757d;
  margin-bottom: 2px;
}

.production-code {
  font-family: 'Courier New', monospace;
}

.production-more {
  font-size: 11px;
  color: #007bff;
  font-style: italic;
}



.billing-cell {
  font-size: 13px;
}

.billing-method {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 2px;
}

.billing-method i {
  color: #007bff;
}

.advance-limit {
  font-size: 11px;
  color: #6c757d;
}

.actions-cell .btn-group {
  display: flex;
  gap: 5px;
}

.dropdown {
  position: relative;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  min-width: 150px;
  display: none;
}

.dropdown-menu.show {
  display: block;
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 8px 15px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item i {
  margin-right: 8px;
}


/* Animációk */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .project-meta {
    grid-template-columns: 1fr;
  }
}

/* Pénzügyi összefoglaló */
.financial-summary {
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.financial-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #e9ecef;
  transition: all 0.2s ease;
}

.financial-item:hover {
  background: #f1f3f4;
  transform: translateX(2px);
}

.financial-item.advance-status {
  border-left-color: #007bff;
}

.financial-item.project-value {
  border-left-color: #28a745;
}

.financial-item.deposit-status {
  border-left-color: #ffc107;
}

.financial-item.invoice-status {
  border-left-color: #6c757d;
}

.financial-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 6px;
  color: #007bff;
  font-size: 14px;
}

.financial-content {
  flex: 1;
  min-width: 0;
}

.financial-label {
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.financial-amount {
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  padding: 0 6px;
}

.financial-values {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.paid-amount,
.waiting-amount {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
}

.paid-amount {
  color: #28a745;
}

.waiting-amount {
  color: #ffc107;
}

.deposit-status-text {
  font-size: 10px;
  font-weight: 400;
  opacity: 0.8;
  margin-left: 4px;
}

.financial-status {
  display: flex;
  align-items: center;
}

.status-indicator {
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
}

.status-done {
  background: #d4edda;
  color: #155724;
}

.status-pending {
  background: #fff3cd;
  color: #856404;
}

/* Várható elkészülés megjelenítés */
.project-deadline {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #e3f2fd;
  border-radius: 8px;
  margin-top: 15px;
  font-size: 13px;
}

.project-deadline i {
  color: #1976d2;
  font-size: 14px;
}

.deadline-label {
  color: #6c757d;
  font-weight: 500;
}

.deadline-date {
  color: #495057;
  font-weight: 600;
}

/* Responsive finomhangolás */
@media (max-width: 576px) {
  .financial-item {
    padding: 10px;
    gap: 10px;
  }

  .financial-icon {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }

  .financial-values {
    gap: 2px;
  }

  .paid-amount,
  .waiting-amount {
    font-size: 12px;
  }
}

/* Gyors betekintéshez optimalizált színek */
.text-success {
  color: #28a745 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-muted {
  color: #6c757d !important;
}

/* Szűrők konténer */
.filters-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 20px;
}

.search-wrapper {
  width: 100%;
}

.search-input {
  position: relative;
  max-width: 500px;
}

.search-input i {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  font-size: 14px;
}

.search-input input {
  padding-left: 45px;
  height: 45px;
  border-radius: 10px;
  border: 2px solid #e9ecef;
  transition: all 0.2s ease;
}

.search-input input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

/* Státusz szűrő gombok */
.status-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.status-filter-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: 2px solid #e9ecef;
  background: white;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.status-filter-btn:hover {
  border-color: #dee2e6;
  background: #f8f9fa;
  transform: translateY(-1px);
}

.status-filter-btn i {
  font-size: 14px;
}

.status-filter-btn.active {
  border-width: 2px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Új státusz */
.status-filter-btn.status-new {
  color: #1565c0;
}

.status-filter-btn.status-new:hover {
  background: #e3f2fd;
  border-color: #90caf9;
}

.status-filter-btn.status-new.active {
  background: #e3f2fd;
  border-color: #1565c0;
  color: #1565c0;
}

/* Folyamatban státusz */
.status-filter-btn.status-in-progress {
  color: #e65100;
}

.status-filter-btn.status-in-progress:hover {
  background: #fff3e0;
  border-color: #ffb74d;
}

.status-filter-btn.status-in-progress.active {
  background: #fff3e0;
  border-color: #e65100;
  color: #e65100;
}

/* Teljesült státusz */
.status-filter-btn.status-completed {
  color: #2e7d32;
}

.status-filter-btn.status-completed:hover {
  background: #e8f5e9;
  border-color: #81c784;
}

.status-filter-btn.status-completed.active {
  background: #e8f5e9;
  border-color: #2e7d32;
  color: #2e7d32;
}

/* Összes gomb */
.status-filter-btn.active:not(.status-new):not(.status-in-progress):not(.status-completed) {
  background: #007bff;
  border-color: #007bff;
  color: white;
}

.filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.status-filter-btn.active .filter-count {
  background: rgba(255, 255, 255, 0.3);
}

/* Reset gomb */
.reset-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  margin-left: auto;
}

.reset-btn:hover {
  background: #dc3545;
  border-color: #dc3545;
  color: white;
}

/* Termék típusok cella */
.product-types-cell {
  font-size: 13px;
}

.product-type-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.product-type-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: #e3f2fd;
  color: #1565c0;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.product-type-tag i {
  font-size: 10px;
  opacity: 0.7;
}
</style>